import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://tribaltech-backend.onrender.com';

// 3-second confirm-click window for File DMCA. Costs nothing,
// prevents the misclick that re-creates the May 5 false-positive
// incident. Mark Safe stays one-click since it's reversible.
const CONFIRM_MS = 3000;

const S = {
  page:        { padding: '32px 28px', maxWidth: 1280, margin: '0 auto', color: '#ddd', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif' },
  h1:          { fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 4 },
  sub:         { fontSize: 12, color: '#666', marginBottom: 20 },
  panel:       { background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '18px 20px', marginBottom: 20 },
  label:       { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, display: 'block' },
  select:      { width: '100%', maxWidth: 480, background: '#080810', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '9px 12px', fontSize: 13, color: '#fff', fontFamily: 'inherit' },
  countsRow:   { display: 'flex', gap: 28, marginTop: 14, fontSize: 13 },
  countItem:   { display: 'flex', flexDirection: 'column', gap: 2 },
  countNum:    { fontSize: 22, fontWeight: 600 },
  countLbl:    { fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th:          { textAlign: 'left', padding: '10px 12px', color: '#555', fontSize: 10, letterSpacing: '0.07em', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, background: '#0f0f1e' },
  td:          { padding: '10px 12px', color: '#ccc', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' },
  link:        { color: '#4A9EFF', textDecoration: 'none' },
  empty:       { padding: '40px 0', textAlign: 'center', color: '#555', fontSize: 12 },
  btn:         { background: '#A78BFA', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnConfirm:  { background: '#EF4444', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSafe:     { background: 'rgba(255,255,255,0.05)', color: '#aaa', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 5, padding: '6px 12px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' },
  textarea:    { width: '100%', maxWidth: 720, minHeight: 110, background: '#080810', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '9px 12px', fontSize: 12, color: '#fff', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', resize: 'vertical', boxSizing: 'border-box' },
  noteInput:   { width: '100%', maxWidth: 720, background: '#080810', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#fff', fontFamily: 'inherit', boxSizing: 'border-box', marginTop: 8 },
  ingestBtn:   { background: '#A78BFA', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 12 },
  ingestBtnDis:{ background: 'rgba(167,139,250,0.4)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 12, fontWeight: 600, cursor: 'not-allowed', marginTop: 12 },
  hint:        { fontSize: 11, color: '#666', marginTop: 6 },
  // Status badges. Semantic split (2026-05-16): green is reserved for
  // "the host acted" (reply_status='removed'). Filed-without-response
  // splits into three sub-states (2026-06-05) so a phantom-filed row
  // can't masquerade as a real send: confirmed_sent (subtle green check
  // + days-since-sent), unverified (amber warning — no send evidence
  // when the era SHOULD have it), unknowable (muted — filed before
  // send-tracking was instrumented, can't determine).
  badgeSent:        { fontSize: 10, color: '#86c9a1', background: 'rgba(52,211,153,0.06)', padding: '3px 8px', borderRadius: 4 },
  badgeUnverified:  { fontSize: 10, color: '#FB923C', background: 'rgba(251,146,60,0.12)', padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  badgeUnknowable:  { fontSize: 10, color: '#777',    background: 'rgba(255,255,255,0.03)', padding: '3px 8px', borderRadius: 4, fontStyle: 'italic' },
  badgeRemoved:  { fontSize: 10, color: '#34D399', background: 'rgba(52,211,153,0.12)', padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  // 2026-10-05: Gone independently of our action — muted blue-grey to
  // distinguish from the removal-win green. Reads as "closed, not won."
  badgeGoneIndependent: { fontSize: 10, color: '#94A3B8', background: 'rgba(148,163,184,0.12)', padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  // 2026-10-06: scanner-duplicate row (duplicate_of IS NOT NULL).
  // Shown admin-side only — creator queries now filter these out via
  // routes/leaks.js applyVisibility.
  badgeDuplicate: { fontSize: 10, color: '#9CA3AF', background: 'rgba(156,163,175,0.08)', padding: '2px 6px', borderRadius: 3, fontWeight: 500 },
  badgeContested:{ fontSize: 10, color: '#F59E0B', background: 'rgba(245,158,11,0.12)', padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  badgeBounce:   { fontSize: 10, color: '#EF4444', background: 'rgba(239,68,68,0.12)',  padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  badgeAcked:    { fontSize: 10, color: '#60A5FA', background: 'rgba(96,165,250,0.12)', padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  // info_required (classifier v3, 2026-10-01): host asked us to re-file
  // with a missing § 512(c)(3) element or a specific direct URL. Not
  // adverse — actionable. Rendered violet to distinguish from the
  // amber "contested" (adverse pushback).
  badgeInfoReq:  { fontSize: 10, color: '#A78BFA', background: 'rgba(167,139,250,0.12)', padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  badgePending:  { fontSize: 10, color: '#FB923C', background: 'rgba(251,146,60,0.04)', padding: '3px 8px', borderRadius: 4 },
  badgeSafe:     { fontSize: 10, color: '#888', background: 'rgba(255,255,255,0.04)', padding: '3px 8px', borderRadius: 4 },
  // Sortable column headers — cursor + active highlight + caret.
  thSortable:    { cursor: 'pointer', userSelect: 'none' },
  thActive:      { color: '#aaa' },
  caret:         { color: '#A78BFA', marginLeft: 4, fontSize: 9 },
  // 2026-10-19: Route-class color coding. Palette matches the status
  // badges (green = win, amber = needs manual, red = no route, grey =
  // closed / already filed). Rendered as a 3px left border on each
  // <tr> plus a text label in the Route column. The left border is
  // non-destructive (status badges keep their own column).
  // ─── Full state machine (2026-10-26 fix (b)) ──────────────────────
  // GREEN  = PLATFORM_DMCA_EMAILS host, un-actioned → File DMCA sends
  //          end-to-end. Highest urgency: ready to click.
  // AMBER  = PLATFORM_DMCA_FORMS only, un-actioned → dmca_fire short-
  //          circuits with send_skipped=no_route; operator submits
  //          webform manually.
  // RED    = no route (neither map), OR host in DMCA_BLACKHOLE_HOSTS.
  //          Needs escalation / alternative-channel strategy.
  // TEAL   = filed with has_notice_target AND notice is FRESH (≤30d
  //          since latest_sent_at). "Action taken, outcome pending" —
  //          nothing for operator to do until the window closes.
  //          Row label: "pending Nd" (days since latest_sent_at).
  // GREY   = removed_at IS NOT NULL (truly gone — our notice confirmed,
  //          or a verification probe stamped found_gone_on_check).
  //
  // Overlay tags on green/amber/red (never on teal/grey):
  //   • "filed?" amber tag  → phantom-filed (dmca_filed=true but no
  //                           dmca_notice_targets backing).
  //   • "legacy, unverified" grey tag → pre-tracking filed rows with
  //                                     no reconstructible evidence.
  //   • "sent, no response" violet tag → has_notice_target AND
  //                                      latest_sent_at >30d AND not
  //                                      removed. The STALE counterpart
  //                                      of TEAL (same filed rows, but
  //                                      past the 30d window).
  //
  // ─── TEAL ↔ VIOLET mutex at the 30d boundary ─────────────────────
  // isNoticeFresh(leak, nowMs): (nowMs - latest_sent_at) ≤ 30d.
  // At exactly 30d: isNoticeFresh=TRUE → TEAL wins (routeClass returns
  //   'pending'); isSentNoResponse returns FALSE (violet suppressed).
  // At 30d + 1ms:   isNoticeFresh=FALSE → routeClass returns route
  //   color (green/amber/red); isSentNoResponse returns TRUE (violet
  //   tag fires on top). Clean handoff, no overlap, no gap.
  // Operator rule: TEAL at exactly 30d — pending ≤30d means ≤30d.
  //
  // ─── 2026-10-26 fix history ──────────────────────────────────────
  // Pre-fix: GREY fired on EITHER removed_at IS NOT NULL OR filed-
  // fresh. Fix (a) dropped the filed-fresh clause so GREY now only
  // means "truly removed." Fix (b) adds TEAL as the honest "filed-
  // pending" signal — a creator sees that action was taken + how long
  // we've been waiting, rather than the row vanishing into GREY or
  // misreading GREEN as "still un-actioned."
  //
  // Chip-count math: routeCounts[c] += 1 is a single assignment per
  // row; adding 'pending' moves rows between buckets, never double-
  // counts. The three overlay chips (phantom_filed, legacy_unverified,
  // sent_no_response) stay separate from the color buckets by design.
  routeBorderGreen:   { borderLeft: '3px solid #34D399' },
  routeBorderAmber:   { borderLeft: '3px solid #F59E0B' },
  routeBorderRed:     { borderLeft: '3px solid #EF4444' },
  routeBorderPending: { borderLeft: '3px solid #38BDF8' },  // teal — filed ≤30d, outcome pending
  routeBorderGrey:    { borderLeft: '3px solid #555'   },
  routeLabelGreen:    { fontSize: 10, color: '#34D399', fontWeight: 600 },
  routeLabelAmber:    { fontSize: 10, color: '#F59E0B', fontWeight: 600 },
  routeLabelRed:      { fontSize: 10, color: '#EF4444', fontWeight: 600 },
  routeLabelPending:  { fontSize: 10, color: '#38BDF8', fontWeight: 600 },
  routeLabelGrey:     { fontSize: 10, color: '#888' },
  // "filed?" tag — tiny amber token appended to the Route cell when the
  // row is phantom-filed (dmca_filed=true but no dmca_notice_targets
  // backing). Signals "this looks filed but we have no send evidence."
  badgePhantomTag:  { fontSize: 9, color: '#F59E0B', background: 'rgba(245,158,11,0.14)', padding: '1px 5px', borderRadius: 3, marginLeft: 6, fontWeight: 600, letterSpacing: '0.03em' },
  // "legacy, unverified" tag — tiny grey-neutral token for pre-tracking
  // filed rows with NO reconstructible send evidence. Not phantom (the
  // schema wasn't tracking yet), but also not auditable. Doesn't grey
  // the row — just surfaces the state so it can't silently pass as filed.
  badgeLegacyUnverifiedTag: { fontSize: 9, color: '#9CA3AF', background: 'rgba(156,163,175,0.14)', padding: '1px 5px', borderRadius: 3, marginLeft: 6, fontWeight: 600, letterSpacing: '0.03em' },
  // "sent, no response" tag — sent notice >30d old, no removal yet.
  // Row keeps its route color (green/amber/red); this surfaces the
  // staleness so the operator can prioritize re-notice / escalation.
  badgeSentNoResponseTag: { fontSize: 9, color: '#C084FC', background: 'rgba(192,132,252,0.14)', padding: '1px 5px', borderRadius: 3, marginLeft: 6, fontWeight: 600, letterSpacing: '0.03em' },
  // Include-safe toggle in the filter bar. is_safe=true rows are
  // excluded from the colored queue + counts by default; this chip
  // flips the exclusion so the operator can audit the safe set.
  includeSafeChip:  { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '5px 12px', fontSize: 11, color: '#777', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1.4 },
  includeSafeOn:    { background: 'rgba(136,136,136,0.14)', border: '1px solid rgba(136,136,136,0.4)', color: '#ddd' },
  toast:       { position: 'fixed', bottom: 24, right: 24, padding: '12px 18px', borderRadius: 6, fontSize: 13, color: '#fff', boxShadow: '0 6px 30px rgba(0,0,0,0.4)', zIndex: 100 },

  // ─── Domain Lookup Console — modal + sub-blocks ──────────────
  btnLookup:        { background: 'rgba(255,255,255,0.05)', color: '#aaa', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 5, padding: '6px 12px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' },
  modalBackdrop:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 200, paddingTop: 60, paddingBottom: 60, overflowY: 'auto' },
  modalPanel:       { background: '#0a0a14', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, width: '100%', maxWidth: 680, padding: 0, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' },
  modalHeader:      { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  modalTitle:       { fontSize: 15, fontWeight: 600, color: '#fff', margin: 0, fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' },
  modalSubtitle:    { fontSize: 11, color: '#666', marginTop: 4 },
  modalClose:       { background: 'transparent', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 },
  modalBody:        { padding: '14px 22px 22px' },
  refetchLink:      { color: '#A78BFA', cursor: 'pointer', textDecoration: 'underline', marginLeft: 8, fontSize: 11 },
  spinnerWrap:      { display: 'flex', alignItems: 'center', gap: 10, padding: '24px 0', justifyContent: 'center', color: '#888', fontSize: 12 },

  // CDN-fronted banner — yellow/orange warning at TOP, with the
  // "below fields show CDN's data" reminder at the BOTTOM of the
  // banner anchored right above the fields it's about.
  cdnBanner:        { background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.35)', borderRadius: 6, padding: '14px 16px', marginBottom: 18 },
  cdnBannerTitle:   { fontSize: 13, color: '#FB923C', fontWeight: 700, marginBottom: 6 },
  cdnBannerNote:    { fontSize: 12, color: '#FDC994', lineHeight: 1.5, margin: 0 },
  cdnBannerFooter:  { fontSize: 11, color: '#FB923C', marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(251,146,60,0.3)', fontStyle: 'italic' },

  // Authoritative section
  sectionHeader:    { fontSize: 11, color: '#aaa', fontWeight: 600, letterSpacing: '0.08em', margin: '4px 0 2px' },
  sectionSub:       { fontSize: 10, color: '#666', margin: '0 0 12px', fontStyle: 'italic' },
  fieldRow:         { display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, padding: '5px 0', alignItems: 'baseline' },
  fieldLabel:       { fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' },
  fieldValue:       { fontSize: 12, color: '#ddd', wordBreak: 'break-word', display: 'flex', alignItems: 'center', gap: 8 },
  fieldEmpty:       { color: '#444' },
  fieldNotFetched:  { color: '#888', fontStyle: 'italic', fontSize: 11 },
  fieldDivider:     { border: 'none', borderTop: '1px solid rgba(255,255,255,0.05)', margin: '10px 0' },
  copyBtn:          { background: 'transparent', border: 'none', cursor: 'pointer', color: '#666', fontSize: 12, padding: '0 4px' },

  // Discovered (scraped) — boxed-off, tinted, prose note as the signal.
  // No copy-to-clipboard inside — scraped emails must keep their
  // verify-before-filing caveat attached, so the friction stays.
  discoveredBox:    { background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 6, padding: '14px 16px', marginTop: 18 },
  discoveredHeader: { fontSize: 11, color: '#F59E0B', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 },
  discoveredNote:   { fontSize: 11, color: '#C8A668', lineHeight: 1.55, margin: '0 0 12px' },

  // Lookup errors — informational, NOT red. Operator should read
  // "couldn't fetch", not "failure."
  errorsBox:        { background: 'rgba(96,165,250,0.04)', border: '1px solid rgba(96,165,250,0.20)', borderRadius: 6, padding: '12px 14px', marginTop: 18, fontSize: 11 },
  errorsHeader:     { fontSize: 11, color: '#60A5FA', fontWeight: 700, marginBottom: 4 },
  errorsSub:        { fontSize: 10, color: '#7BA7CC', margin: '0 0 8px', fontStyle: 'italic' },
  errorsItem:       { fontSize: 11, color: '#aaa', padding: '2px 0' },

  // Whole-lookup error (the call failed, distinct from per-source lookup_errors)
  lookupErrorBox:   { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 6, padding: '14px 16px', color: '#FCA5A5', fontSize: 12 },

  // ─── Filter bar (status chips + host filter + count) ─────────
  filterPanel:     { background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '14px 20px', marginBottom: 20 },
  chipRow:         { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  chip:            { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '5px 12px', fontSize: 11, color: '#aaa', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1.4 },
  chipActive:      { background: 'rgba(167,139,250,0.18)', border: '1px solid rgba(167,139,250,0.5)', color: '#E9DEFE' },
  chipCount:       { fontSize: 10, color: '#666', background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: 8, minWidth: 16, textAlign: 'center' },
  chipCountActive: { color: '#A78BFA' },
  filterCount:     { fontSize: 11, color: '#888', marginLeft: 'auto' },
  hostFilterRow:   { display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 },
  hostSelect:      { background: '#080810', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '7px 10px', fontSize: 12, color: '#fff', fontFamily: 'inherit', minWidth: 240 },
  clearBtn:        { background: 'rgba(255,255,255,0.05)', color: '#aaa', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 5, padding: '6px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
};

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function host(u) { try { return new URL(u).hostname.replace('www.', ''); } catch { return '—'; } }
function adminKey() { return sessionStorage.getItem('cp_admin_key') || ''; }
function adminHeaders(extra) { return { 'X-Admin-Key': adminKey(), ...(extra || {}) }; }

// ─── Domain Lookup Console — helpers + sub-components ──────────
// Render the lookup payload from POST /api/admin/domain-intel/lookup
// honestly: authoritative block (RDAP/DNS/RIPE — use for filing),
// discovered block (page-scraped — verify before filing), cdn_fronted
// banner when masked, lookup_errors block when per-source fetches
// hit problems. The payload encodes the honesty; this rendering's
// job is to NOT flatten it.

function fmtDateOnly(iso) {
  if (!iso) return null;
  try { return new Date(iso).toISOString().slice(0, 10); }
  catch { return iso; }
}

function summarizeMx(mxRecords) {
  if (!Array.isArray(mxRecords) || mxRecords.length === 0) return null;
  const allGoogle = mxRecords.every(m => /google\.com$/i.test(m.exchange || ''));
  if (allGoogle) return `Google Workspace (${mxRecords.length} record${mxRecords.length === 1 ? '' : 's'})`;
  return mxRecords
    .slice()
    .sort((a, b) => a.priority - b.priority)
    .map(m => `${m.priority} ${m.exchange}`)
    .join(', ');
}

// One label-value row. hasError=true displays "(not fetched — see
// issues below)" instead of a blank, distinguishing the two cases
// (per the lookup_errors honesty rule).
function FieldRow({ label, value, copyable, onCopy, hasError }) {
  return (
    <div style={S.fieldRow}>
      <div style={S.fieldLabel}>{label}</div>
      <div style={S.fieldValue}>
        {hasError ? (
          <span style={S.fieldNotFetched}>(not fetched — see issues below)</span>
        ) : value ? (
          <>
            <span>{value}</span>
            {copyable && (
              <button style={S.copyBtn} onClick={onCopy} title="Copy to clipboard">📋</button>
            )}
          </>
        ) : (
          <span style={S.fieldEmpty}>—</span>
        )}
      </div>
    </div>
  );
}

function DomainLookupModal({ lookup, onClose, onRefetch, onRetry, onCopy }) {
  const data = lookup.data;
  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modalPanel} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <h3 style={S.modalTitle}>Domain Lookup: {lookup.domain}</h3>
            {data && (
              <div style={S.modalSubtitle}>
                {data._cached
                  ? `Cached · ${data._cache_age_hours}h old`
                  : 'Fetched just now · fresh'}
                <span style={S.refetchLink} onClick={onRefetch}>↻ Re-fetch</span>
              </div>
            )}
          </div>
          <button style={S.modalClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={S.modalBody}>
          {lookup.loading && (
            <div style={S.spinnerWrap}>
              <span style={{ fontSize: 18 }}>⏳</span>
              <span>Looking up… (3-4s typical)</span>
            </div>
          )}

          {!lookup.loading && lookup.error && (
            <div style={S.lookupErrorBox}>
              <strong>⚠ Lookup failed:</strong> {lookup.error}
              <div style={{ marginTop: 10 }}>
                <button style={S.btn} onClick={onRetry}>Retry</button>
              </div>
            </div>
          )}

          {!lookup.loading && data && (
            <>
              {/* CDN-fronted banner — TOP, with the "below fields show
                  CDN's data, do not file at the CDN" reinforcement at
                  the BOTTOM of the banner anchored right above the
                  fields it's about. */}
              {data.cdn_fronted && data.cdn_fronted.is_cdn_fronted && (
                <div style={S.cdnBanner}>
                  <div style={S.cdnBannerTitle}>
                    ⚠ CDN-FRONTED: {data.cdn_fronted.cdn_name}
                  </div>
                  <p style={S.cdnBannerNote}>{data.cdn_fronted.masking_note}</p>
                  <div style={S.cdnBannerFooter}>
                    ── Below fields show {data.cdn_fronted.cdn_name}'s data, NOT the origin's. Do not file at the CDN. ──
                  </div>
                </div>
              )}

              {/* AUTHORITATIVE — RDAP / DNS / RIPE Stat. Use for filing. */}
              <h4 style={S.sectionHeader}>── AUTHORITATIVE ──</h4>
              <p style={S.sectionSub}>RDAP / DNS / RIPE Stat — use these for filing</p>

              <FieldRow
                label="Registrar"
                value={data.authoritative.registrar}
                hasError={!!(data.lookup_errors && data.lookup_errors.rdap) && !data.authoritative.registrar}
              />
              <FieldRow
                label="Registrar abuse"
                value={data.authoritative.registrar_abuse_email}
                copyable
                onCopy={() => onCopy(data.authoritative.registrar_abuse_email, 'Registrar abuse')}
                hasError={!!(data.lookup_errors && data.lookup_errors.rdap) && !data.authoritative.registrar_abuse_email}
              />
              {data.authoritative.registrar_abuse_phone && (
                <FieldRow label="Registrar phone" value={data.authoritative.registrar_abuse_phone} />
              )}
              <FieldRow
                label="Domain status"
                value={Array.isArray(data.authoritative.domain_status) ? data.authoritative.domain_status.join(', ') : null}
                hasError={!!(data.lookup_errors && data.lookup_errors.rdap) && !data.authoritative.domain_status}
              />
              <FieldRow label="Registered" value={fmtDateOnly(data.authoritative.domain_created_at)} />
              <FieldRow label="Expires" value={fmtDateOnly(data.authoritative.domain_expires_at)} />

              <hr style={S.fieldDivider} />

              <FieldRow
                label="A records"
                value={(data.authoritative.a_records || []).join(', ')}
                hasError={!!(data.lookup_errors && data.lookup_errors.dns) && !(data.authoritative.a_records || []).length}
              />
              <FieldRow label="Nameservers" value={(data.authoritative.nameservers || []).join(', ')} />
              <FieldRow label="MX" value={summarizeMx(data.authoritative.mx_records)} />

              <hr style={S.fieldDivider} />

              <FieldRow
                label="ASN / Org"
                value={data.authoritative.asn ? `${data.authoritative.asn} / ${data.authoritative.asn_org || '?'}` : null}
                hasError={!!(data.lookup_errors && data.lookup_errors.asn) && !data.authoritative.asn}
              />
              <FieldRow
                label="Host abuse"
                value={data.authoritative.host_abuse_email}
                copyable
                onCopy={() => onCopy(data.authoritative.host_abuse_email, 'Host abuse')}
                hasError={!!(data.lookup_errors && data.lookup_errors.asn) && !data.authoritative.host_abuse_email}
              />

              {/* DISCOVERED — page-scraped, HEURISTIC. The box itself
                  is the signal: this looks different from authoritative
                  on purpose. Scraped emails intentionally have NO copy
                  button — friction is the point per the
                  verify-before-filing semantic. */}
              <div style={S.discoveredBox}>
                <div style={S.discoveredHeader}>⚠ DISCOVERED — HEURISTIC, VERIFY BEFORE FILING</div>
                <p style={S.discoveredNote}>{data.discovered._note}</p>
                <FieldRow
                  label="DMCA page"
                  value={data.discovered.dmca_page_url || '(none found)'}
                />
                <FieldRow
                  label="Emails"
                  value={data.discovered.dmca_emails_found && data.discovered.dmca_emails_found.length
                    ? data.discovered.dmca_emails_found.join(', ')
                    : '(none)'}
                  /* Intentionally NOT copyable — see comment above. */
                />
              </div>

              {/* LOOKUP ERRORS — informational, blue (not red). These
                  mean "couldn't fetch" not "field genuinely empty." */}
              {data.lookup_errors && (
                <div style={S.errorsBox}>
                  <div style={S.errorsHeader}>ℹ Lookup Issues</div>
                  <p style={S.errorsSub}>These mean "we couldn't fetch" — not "field is genuinely empty."</p>
                  {Object.entries(data.lookup_errors).map(([source, msg]) => (
                    <div key={source} style={S.errorsItem}>
                      <strong>{source}</strong>: {typeof msg === 'string' ? msg : JSON.stringify(msg)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Era boundaries for the send-verification detector (derived from
// the 2026-06-05 phantom-filed diagnostic — see
// project_phantom_filed_213_rows in memory + the corpus-wide
// sizing in scope_corpus_phantom.py).
//   Era A: pre-2026-05-15 — reply_token didn't exist on any send
//          path. No in-row send-evidence retrospectively recoverable.
//   Era B: 2026-05-15 → 2026-06-03 — reply_token stamped during the
//          window. Token presence = confirmed; absence = unverified
//          (upper-bound: rollout was incremental across send sites,
//          so some no-token rows in early sub-windows may be real
//          sends from paths not yet retrofitted).
//   Era C: >= 2026-06-03 — Stage 2 cutover. dmca_notice_targets is
//          authoritative. has_notice_target=true = confirmed;
//          false = unverified (the bug is structurally guaranteed
//          on unroutable hosts AND fires on errors on routable hosts).
const SEND_TRACKING_START = '2026-05-15';
const PHASE1_CUTOVER      = '2026-06-03';

// Returns 'confirmed_sent' | 'unverified' | 'unknowable' for filed
// rows, or null when !dmca_filed. The UI uses this to split the
// existing "Filed" badge into three honest sub-states so phantom-
// filed rows can never masquerade as real sends.
//
// 2026-10-01 fix: has_notice_target is now gated upstream (in
// backend/routes/admin.js) on dmca_notices.send_status='sent' —
// ANY route (direct_email, webform, host_abuse) and ANY origin
// (manual_send, pipeline_send, null-origin backfill) count. The
// previous backend query silently failed on creators with >200 rows
// (Bad Request from oversized URL), flipping every filed row to
// 'unverified' in the UI. leak.sent_routes carries the distinct
// route_types so the badge can show "Sent · webform" or
// "Sent · 3 channels" instead of just a check.
function sendVerification(leak) {
  if (!leak.dmca_filed) return null;
  const filed = (leak.dmca_filed_at || '').slice(0, 10);
  if (!filed) return 'unknowable';
  if (filed >= PHASE1_CUTOVER) {
    return leak.has_notice_target ? 'confirmed_sent' : 'unverified';
  }
  if (filed >= SEND_TRACKING_START) {
    return leak.reply_token ? 'confirmed_sent' : 'unverified';
  }
  return 'unknowable';
}

// Days since an ISO timestamp. Returns null on bad input.
function ageDays(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

// Single source of truth for status categorization. Precedence
// (updated 2026-09-29):
//   1. is_safe               — operator dismissal wins over everything.
//                              Even if the host later confirmed removal
//                              on this specific URL, the operator's own
//                              call ("not mine" or "won't pursue") stays
//                              authoritative — Removed information stays
//                              accessible via tooltip. Ratified 2026-09-29.
//   2. removed_at set        — observed removal (from liveness probe OR
//                              inherited from sibling OR host reply).
//                              Wins ahead of reply_status because
//                              removed_at is the authoritative "gone"
//                              column; reply_status is "what host SAID"
//                              (Task 4 constraint 2026-09-29). Fixes the
//                              RzbQf3lR5Oz stale-Contested surface bug.
//   3. reply_status          — what the host SAID (removed > acknowledged >
//                              contested > bounce). 'removed' entry here
//                              covers the case where reply_status is set
//                              but removed_at hasn't been stamped yet.
//   4. dmca_filed_at         — sent, no reply yet.
//   5. else                  — Unverified (formerly "pending"). Nothing
//                              tried yet OR reply_status not set on filed
//                              rows without dmca_filed_at.
//
// "filed" here is the parent state. The send-verification sub-states
// (confirmed_sent / unverified / unknowable) live in sendVerification(leak)
// and split the "filed" badge in the UI. The Phantom-Filed chip filter
// targets sendVerification === 'unverified' directly, NOT leakStatus.
function leakStatus(leak) {
  if (leak.is_safe === true)                 return 'safe';
  // 2026-10-05: removal_source='found_gone_on_check' means the URL went
  // gone independently of our action (recheck confirmed 404/410 but no
  // sent notice is in play). Show as a distinct "gone independently"
  // state and keep it OUT of the "Removed" count. Pre-migration
  // (column missing): leak.removal_source === undefined → falls through
  // to the 'removed' branch, preserving legacy behavior.
  if (leak.removed_at && leak.removal_source === 'found_gone_on_check') return 'gone_independently';
  if (leak.removed_at)                       return 'removed';
  if (leak.reply_status === 'removed')       return 'removed';
  if (leak.reply_status === 'acknowledged')  return 'acknowledged';
  // info_required ranks ABOVE contested in triage priority — it's
  // actionable (re-file with the missing field) vs adverse (no action
  // path). Classifier v3 (2026-10-01) introduced this status.
  if (leak.reply_status === 'info_required') return 'info_required';
  if (leak.reply_status === 'contested')     return 'contested';
  if (leak.reply_status === 'bounce')        return 'bounce';
  if (leak.dmca_filed_at)                    return 'filed';
  return 'unverified';
}

// Triage priority — lowest = most operator-attention-needed. Used
// as the sort key when the Status column is active. Sort ascending
// puts Pending/Bounce/Contested at the top (where you want to act);
// descending puts Removed/Safe at the top (where you want to
// confirm closeouts).
const STATUS_RANK = {
  unverified: 1, bounce: 2, info_required: 3, contested: 4, filed: 5, acknowledged: 6, removed: 7, gone_independently: 7.5, safe: 8,
};
function statusRank(leak) { return STATUS_RANK[leakStatus(leak)]; }

// Filter chip definitions — ordered to match the operator's mental
// queue: All on the left, then triage priority (Pending first,
// Safe last). Keys match leakStatus() return values exactly.
const STATUS_FILTERS = [
  { key: 'all',           label: 'All' },
  { key: 'unverified',    label: 'Unverified' },     // NEW top-level state (was 'pending')
  { key: 'filed',         label: 'Filed' },
  { key: 'phantom_filed', label: 'Phantom-Filed' },  // sub-filter of Filed (was key 'unverified')
  { key: 'removed',           label: 'Removed' },
  { key: 'gone_independently', label: 'No longer online' },
  { key: 'acknowledged',      label: 'Acknowledged' },
  { key: 'info_required', label: 'Info requested' },
  { key: 'contested',     label: 'Contested' },
  { key: 'bounce',        label: 'Bounce' },
  { key: 'safe',          label: 'Safe' },
];

// 2026-10-19: Route-class color code — 4-state (plus phantom-filed
// sub-state). Backend route_class ∈ {'email','form','none'} is combined
// here with dmca_filed + has_notice_target + removed_at to produce the
// row's displayed color. The grey override is intentionally gated on
// has_notice_target so phantom-filed rows (dmca_filed=true, 0 targets)
// keep their route color — they're not actually filed in any auditable
// sense, so greying them would hide the triage signal.
//
// is_safe=true rows are EXCLUDED from the colored queue entirely (see
// filtered-rows pipeline below). This helper returns null for them so
// a caller that forgets to pre-filter gets a null rather than a wrong
// color.
// 2026-10-20: 30-day staleness window on Grey. Operator-approved
// rule change: a filed-with-target row only Greys if the latest
// sent notice is ≤30d old. Older → row keeps its route color +
// surfaces via the sent_no_response tag/chip (defined below). The
// backend aggregator in routes/admin.js applies the same window.
const GREY_STALE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
function isNoticeFresh(leak, nowMs) {
  if (!leak || !leak.latest_sent_at) return false;
  const t = Date.parse(leak.latest_sent_at);
  if (isNaN(t)) return false;
  return (nowMs - t) <= GREY_STALE_WINDOW_MS;
}
function routeClass(leak) {
  if (!leak) return null;
  if (leak.is_safe === true) return null;  // excluded from the queue
  // GREY = truly gone (removed_at stamped).
  if (leak.removed_at) return 'grey';
  // TEAL = filed with has_notice_target AND notice is fresh (≤30d).
  // "Pending outcome" — we've acted, waiting for host response. At
  // >30d this falls through to the route color + sent_no_response
  // violet tag (see isSentNoResponse at line ~604). Clean mutex at
  // the boundary: isNoticeFresh returns true for ≤30d (TEAL wins at
  // exactly 30d); isSentNoResponse is its strict complement.
  if (leak.dmca_filed === true
      && leak.has_notice_target === true
      && isNoticeFresh(leak, Date.now())) return 'pending';
  // Fall through to the backend-computed route class.
  switch (leak.route_class) {
    case 'email': return 'green';
    case 'form':  return 'amber';
    default:      return 'red';
  }
}
// 2026-10-20: phantom-filed definition unified across the status
// chip (statusCounts.phantom_filed) + the route chip
// (routeCounts.phantom_filed) + the row's "filed?" tag. Single
// source of truth = sendVerification() which is era-aware
// (PHASE1_CUTOVER / SEND_TRACKING_START). The previous route-chip
// definition was era-agnostic and double-counted pre-tracking legacy
// rows (1,302 extra at global scale, ~135 per creator). Operator
// approved the narrower definition 2026-10-20.
function isPhantomFiled(leak) {
  if (!leak) return false;
  if (leak.is_safe === true) return false;
  if (leak.removed_at) return false;
  return leakStatus(leak) === 'filed' && sendVerification(leak) === 'unverified';
}
// Legacy-unverified: dmca_filed=true in a pre-tracking era (before
// 2026-05-15 OR in SendTracking era without a reply_token) AND no
// reconstructible send evidence (no reply_token, no filing_method,
// no filed_by, no "DMCA filed to" / "filed to abuse" in notes).
// These are NOT phantoms — the schema wasn't tracking yet — but they
// also silently pass as "filed" today without a visible flag. The
// new tag + chip surfaces them. Rows WITH reconstructible evidence
// route to the backfill workstream
// (outbound_drafts/phantom_filed_backfill_worklist.md) and are NOT
// flagged legacy-unverified.
function hasReconstructibleEvidence(leak) {
  if (!leak) return false;
  if (leak.reply_token) return true;
  if (leak.filing_method) return true;
  if (leak.filed_by) return true;
  const n = (leak.notes || '').toLowerCase();
  if (n.includes('dmca filed to') || n.includes('filed to abuse')) return true;
  return false;
}
function isLegacyUnverified(leak) {
  if (!leak) return false;
  if (leak.is_safe === true) return false;
  if (leak.removed_at) return false;
  if (leak.dmca_filed !== true) return false;
  if (sendVerification(leak) !== 'unknowable') return false;
  return !hasReconstructibleEvidence(leak);
}
// 2026-10-20: a row is "sent, no response" when it has a real sent
// notice (has_notice_target=true) + latest_sent_at >30d ago + not
// removed + not safe. These keep their route color (green/amber/red
// per the host's route) and get the sent_no_response tag/chip so the
// operator can prioritize re-notice or escalation. This matches the
// backend aggregator's sentNoResponse flag.
function isSentNoResponse(leak) {
  if (!leak) return false;
  if (leak.is_safe === true) return false;
  if (leak.removed_at) return false;
  if (leak.dmca_filed !== true) return false;
  if (leak.has_notice_target !== true) return false;
  return !isNoticeFresh(leak, Date.now());
}
// Triage priority for the Route column sort — lowest = most-actionable.
// RED first (no route — needs operator decision), AMBER (needs manual
// send), GREEN (click File DMCA), PENDING (filed ≤30d, nothing to do
// until window closes), GREY (truly removed).
const ROUTE_RANK = { red: 1, amber: 2, green: 3, pending: 4, grey: 5 };
function routeRank(leak) {
  const c = routeClass(leak);
  return c ? ROUTE_RANK[c] : 999;  // is_safe=null → sinks to bottom
}

// Route-class chip definitions — ordered to match the queue priority
// so RED (most actionable) is on the left and GREY on the right.
const ROUTE_CLASS_FILTERS = [
  { key: 'all',     label: 'All routes' },
  { key: 'red',     label: 'Red (no route)' },
  { key: 'amber',   label: 'Amber (form)' },
  { key: 'green',   label: 'Green (email)' },
  { key: 'pending', label: 'Pending (≤30d)' },  // teal — filed, awaiting outcome within the 30d window
  { key: 'grey',    label: 'Grey (removed)' },
  { key: 'phantom_filed',     label: 'Phantom-filed' },      // sub-filter: dmca_filed w/o target (Phase-1 era, era-aware)
  { key: 'legacy_unverified', label: 'Legacy, unverified' }, // sub-filter: pre-tracking filed row, no reconstructible evidence
  { key: 'sent_no_response',  label: 'Sent, no response' },  // sub-filter: has_notice_target + latest_sent_at >30d, not removed
];

// Sensible default direction per column. URL/Host alphabetical;
// Detected newest-first; Status urgent-first. Route most-actionable-first.
const DEFAULT_SORT_DIR = {
  leaked_url:  'asc',
  host:        'asc',
  detected_at: 'desc',
  status:      'asc',
  routeClass:  'asc',
};

function sortLeaks(leaks, key, dir) {
  const mult = dir === 'asc' ? 1 : -1;
  const arr = [...leaks];
  arr.sort((a, b) => {
    let av, bv;
    if      (key === 'host')       { av = host(a.leaked_url); bv = host(b.leaked_url); }
    else if (key === 'status')     { av = statusRank(a);      bv = statusRank(b); }
    else if (key === 'routeClass') { av = routeRank(a);       bv = routeRank(b); }
    else                           { av = a[key];             bv = b[key]; }
    // Nulls always sink to the bottom regardless of direction —
    // info-poor rows shouldn't dominate either end of a sort.
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return -1 * mult;
    if (av > bv) return  1 * mult;
    return 0;
  });
  return arr;
}

function SortableTh({ label, sortKey, currentKey, currentDir, onSort, style }) {
  const active = currentKey === sortKey;
  const caret  = active ? (currentDir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      style={{ ...S.th, ...S.thSortable, ...(active ? S.thActive : null), ...style }}
      onClick={() => onSort(sortKey)}
      title={`Sort by ${label}`}
    >
      {label}{active && <span style={S.caret}>{caret}</span>}
    </th>
  );
}

export default function AdminLeaks() {
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [creators, setCreators] = useState([]);
  const [creator, setCreator] = useState(null);
  const [leaks, setLeaks] = useState([]);
  const [counts, setCounts] = useState({ total: 0, dmca_filed: 0, marked_safe: 0, unfiled: 0 });

  // Domain Lookup Console — null when closed; { domain, loading,
  // data, error } when open. See DomainLookupModal + openDomainLookup.
  const [lookup, setLookup] = useState(null);
  // Index-mode: per-creator counts aggregated from /api/admin/overview.
  // Map<creator_id, {total, unfiled, marked_safe}>.
  const [indexCounts, setIndexCounts] = useState(null);
  // 2026-10-19: aggregate route_counts across ALL creators from
  // /api/admin/overview. Null until the fetch resolves. Used only
  // in no-creator mode; per-creator view computes its own counts
  // locally from the fetched leaks.
  const [overviewRouteCounts, setOverviewRouteCounts] = useState(null);
  const [overviewRouteCountsIncludingSafe, setOverviewRouteCountsIncludingSafe] = useState(null);
  const [overviewSafeCount, setOverviewSafeCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  // Map<leakId, {confirmAt: number}> for in-flight 3-second confirm windows.
  const [pendingFire, setPendingFire] = useState({});
  const tickRef = useRef(null);
  // Paste-ingest panel state. Lives only while a creator is selected.
  const [pasteUrls, setPasteUrls] = useState('');
  const [pasteNote, setPasteNote] = useState('');
  const [ingesting, setIngesting] = useState(false);
  // Sort state for the leak table. Default matches the backend's
  // ORDER BY detected_at DESC so the initial render is identical
  // to pre-sort behaviour.
  const [sortKey, setSortKey] = useState('detected_at');
  const [sortDir, setSortDir] = useState('desc');

  // Filter state. All reset when switching creators — filters are
  // per-creator-data scoped (a host present in creator A probably
  // isn't present in creator B, and the status/route mix differs).
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterHost,   setFilterHost]   = useState('all');
  // 2026-10-19: route-class filter + is_safe toggle. By default
  // is_safe=true rows are excluded from the colored queue (and counts);
  // includeSafe=true flips that so the operator can audit the safe set.
  const [filterRoute,  setFilterRoute]  = useState('all');
  const [includeSafe,  setIncludeSafe]  = useState(false);
  useEffect(() => {
    setFilterStatus('all');
    setFilterHost('all');
    setFilterRoute('all');
    setIncludeSafe(false);
  }, [creatorId]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(DEFAULT_SORT_DIR[key] || 'asc');
    }
  }

  // Domain Lookup Console — open modal + fetch. force=true bypasses
  // the 4-day TTL cache (UI's ↻ Re-fetch link); default false uses
  // cache when fresh. Errors surface in two places: per-source
  // problems land in data.lookup_errors and render in their own
  // (blue) box; whole-call failures (network / 403 / 500) land in
  // lookup.error and render as the red banner with a Retry button.
  async function openDomainLookup(leak, force = false) {
    const domain = host(leak.leaked_url);
    setLookup({ domain, loading: true, data: null, error: null, _leak: leak });
    try {
      const r = await fetch(`${API}/api/admin/domain-intel/lookup`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ domain, force }),
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        if (r.status === 403) msg = 'Auth failed — admin key missing or rejected.';
        else { try { const j = await r.json(); if (j.error) msg = j.error; } catch {} }
        setLookup(prev => prev ? { ...prev, loading: false, error: msg } : prev);
        return;
      }
      const data = await r.json();
      setLookup(prev => prev ? { ...prev, loading: false, data, error: null } : prev);
    } catch (e) {
      setLookup(prev => prev ? { ...prev, loading: false, error: e.message || 'network error' } : prev);
    }
  }

  // Copy to clipboard (used by authoritative-block emails only —
  // scraped emails in the discovered block intentionally have NO
  // copy button per the verify-before-filing semantic). Toast on
  // success so the operator sees it landed.
  function copyAuthoritative(text, label) {
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => setToast({ kind: 'ok', text: `Copied: ${label}` }),
        () => setToast({ kind: 'err', text: 'Copy failed' }),
      );
    } else {
      setToast({ kind: 'err', text: 'Clipboard API unavailable' });
    }
  }

  // Escape-key close for the lookup modal. Listener attaches only
  // while lookup !== null so we don't pollute global key handling
  // on the normal page.
  useEffect(() => {
    if (!lookup) return;
    const onKey = (e) => { if (e.key === 'Escape') setLookup(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lookup]);

  // Re-render every 200ms while any confirm window is open so the
  // countdown text updates. Cleared as soon as nothing is pending.
  useEffect(() => {
    if (Object.keys(pendingFire).length === 0) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    if (!tickRef.current) {
      tickRef.current = setInterval(() => {
        setPendingFire(prev => {
          const now = Date.now();
          const next = {};
          for (const [k, v] of Object.entries(prev)) {
            if (v.confirmAt > now) next[k] = v;
          }
          return next;
        });
      }, 200);
    }
    return () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };
  }, [pendingFire]);

  // Auth gate: redirect to /admin if no admin key in session.
  useEffect(() => {
    if (!adminKey()) {
      navigate('/admin', { replace: true });
      return;
    }
    setAuthChecked(true);
  }, [navigate]);

  // Load creator list once.
  useEffect(() => {
    if (!authChecked) return;
    fetch(`${API}/api/admin/creators/list`, { headers: adminHeaders() })
      .then(r => r.json())
      .then(d => setCreators(d.creators || []))
      .catch(() => setCreators([]));
  }, [authChecked]);

  // Index-mode: fetch /api/admin/overview and aggregate per-creator
  // counts client-side. Skipped when a creatorId is set since the
  // per-creator view does its own focused fetch.
  useEffect(() => {
    if (!authChecked || creatorId) return;
    fetch(`${API}/api/admin/overview`, { headers: adminHeaders() })
      .then(r => r.json())
      .then(d => {
        const map = {};
        for (const l of (d.leaks || [])) {
          if (!l.creator_id) continue;
          const c = map[l.creator_id] || { total: 0, unfiled: 0, marked_safe: 0 };
          c.total += 1;
          if (l.is_safe === true) c.marked_safe += 1;
          else if (!l.dmca_filed_at) c.unfiled += 1;
          map[l.creator_id] = c;
        }
        setIndexCounts(map);
        // 2026-10-19: aggregate route_counts for the no-creator chip row.
        setOverviewRouteCounts(d.route_counts || null);
        setOverviewRouteCountsIncludingSafe(d.route_counts_including_safe || null);
        setOverviewSafeCount(d.safe_count || 0);
      })
      .catch(() => {
        setIndexCounts({});
        setOverviewRouteCounts(null);
        setOverviewRouteCountsIncludingSafe(null);
        setOverviewSafeCount(0);
      });
  }, [authChecked, creatorId]);

  const loadLeaks = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/users/${id}/leaks`, { headers: adminHeaders() });
      const d = await r.json();
      setCreator(d.creator || null);
      setLeaks(d.leaks || []);
      setCounts({
        total:       d.total       || 0,
        dmca_filed:  d.dmca_filed  || 0,
        marked_safe: d.marked_safe || 0,
        unfiled:     d.unfiled     || 0,
      });
    } catch (e) {
      setToast({ kind: 'err', text: `Failed to load: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authChecked && creatorId) loadLeaks(creatorId);
  }, [authChecked, creatorId, loadLeaks]);

  // Auto-dismiss toast after 4s.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function pickCreator(e) {
    const id = e.target.value;
    if (id) navigate(`/admin/leaks/${id}`);
  }

  // File DMCA: 3-second confirm-click. First click stages; second
  // click within window fires. Window expires silently — operator
  // can re-click any time.
  //
  // Re-fire guard (added 2026-09-29, strengthened 2026-10-01): if the
  // row already has dmca_filed=true, an extra modal confirm asks
  // explicitly before the 3-second-click dance even starts. Confirm
  // text includes last-filed date and the route(s) actually used
  // (sent_routes from backend admin.js enrichment) so the operator
  // doesn't re-send what already landed on a known channel. Prevents
  // accidental re-fires on Contested/Acknowledged/Bounce/Filed rows
  // now that File DMCA is exposed on every non-terminal state.
  function onFireClick(leak) {
    if (leak.dmca_filed || (Array.isArray(leak.sent_routes) && leak.sent_routes.length > 0)) {
      const filedDate = leak.dmca_filed_at ? fmt(leak.dmca_filed_at) : 'unknown date';
      const hostStatus = leak.reply_status || 'no host reply yet';
      const routeInfo = (Array.isArray(leak.sent_routes) && leak.sent_routes.length)
        ? ` via ${leak.sent_routes.join(' + ')}`
        : '';
      if (!window.confirm(`Already filed on ${filedDate}${routeInfo} (host status: ${hostStatus}). File again?`)) return;
    }
    const now = Date.now();
    const pending = pendingFire[leak.id];
    if (pending && pending.confirmAt > now) {
      // Second click within window — fire.
      fireDmca(leak);
      setPendingFire(prev => { const n = { ...prev }; delete n[leak.id]; return n; });
    } else {
      setPendingFire(prev => ({ ...prev, [leak.id]: { confirmAt: now + CONFIRM_MS } }));
    }
  }

  async function fireDmca(leak) {
    // Optimistic: mark filed locally; rollback on error.
    const prevLeaks = leaks;
    const prevCounts = counts;
    setLeaks(ls => ls.map(l => l.id === leak.id
      ? { ...l, dmca_filed: true, dmca_filed_at: new Date().toISOString() } : l));
    setCounts(c => ({ ...c, dmca_filed: c.dmca_filed + 1, unfiled: Math.max(0, c.unfiled - 1) }));
    try {
      const r = await fetch(`${API}/api/admin/leaks/${leak.id}/fire-dmca`, {
        method: 'POST', headers: adminHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setToast({ kind: 'ok', text: `DMCA fired → ${d.dmca_contact || 'no contact'} (sent: ${d.email_sent})` });
    } catch (e) {
      setLeaks(prevLeaks);
      setCounts(prevCounts);
      setToast({ kind: 'err', text: `Fire failed: ${e.message}` });
    }
  }

  async function markSafe(leak) {
    // Confirm dialog — addresses 2026-09-29 misclick incident where
    // 559bb769 was marked safe reflexively 5s after seeing its send fail,
    // when the row was actually a real leak. Native confirm() is
    // synchronous, blocks the button, and needs no state machinery.
    if (!window.confirm('Mark this as not yours?')) return;
    const prevLeaks = leaks;
    const prevCounts = counts;
    setLeaks(ls => ls.map(l => l.id === leak.id
      ? { ...l, is_safe: true, marked_safe_at: new Date().toISOString() } : l));
    setCounts(c => ({
      ...c,
      marked_safe: c.marked_safe + 1,
      unfiled:     leak.dmca_filed_at ? c.unfiled : Math.max(0, c.unfiled - 1),
    }));
    try {
      const r = await fetch(`${API}/api/admin/leaks/${leak.id}/mark-safe`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reason: 'Operator marked false positive' }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
    } catch (e) {
      setLeaks(prevLeaks);
      setCounts(prevCounts);
      setToast({ kind: 'err', text: `Mark safe failed: ${e.message}` });
    }
  }

  async function ingestUrls() {
    if (!creatorId) return;
    const urls = pasteUrls.split('\n').map(s => s.trim()).filter(Boolean);
    if (urls.length === 0) {
      setToast({ kind: 'err', text: 'Paste at least one URL.' });
      return;
    }
    if (urls.length > 200) {
      setToast({ kind: 'err', text: `Capped at 200 URLs (you pasted ${urls.length}).` });
      return;
    }
    setIngesting(true);
    try {
      const r = await fetch(`${API}/api/admin/leaks/manual-ingest`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          creator_id: creatorId,
          urls,
          source_note: pasteNote.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const parts = [`Added ${d.inserted}`];
      if (d.skipped_duplicate) parts.push(`${d.skipped_duplicate} dupes skipped`);
      if (d.skipped_invalid)   parts.push(`${d.skipped_invalid} invalid skipped`);
      setToast({ kind: 'ok', text: parts.join(' · ') });
      setPasteUrls('');
      setPasteNote('');
      await loadLeaks(creatorId);
    } catch (e) {
      setToast({ kind: 'err', text: `Ingest failed: ${e.message}` });
    } finally {
      setIngesting(false);
    }
  }

  async function unmarkSafe(leak) {
    const prevLeaks = leaks;
    const prevCounts = counts;
    setLeaks(ls => ls.map(l => l.id === leak.id
      ? { ...l, is_safe: false, marked_safe_at: null } : l));
    setCounts(c => ({
      ...c,
      marked_safe: Math.max(0, c.marked_safe - 1),
      unfiled:     leak.dmca_filed_at ? c.unfiled : c.unfiled + 1,
    }));
    try {
      const r = await fetch(`${API}/api/admin/leaks/${leak.id}/unmark-safe`, {
        method: 'POST', headers: adminHeaders(),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
    } catch (e) {
      setLeaks(prevLeaks);
      setCounts(prevCounts);
      setToast({ kind: 'err', text: `Unmark failed: ${e.message}` });
    }
  }

  if (!authChecked) return null;

  // Status counts for chip labels — single O(N) pass over the full
  // set (NOT the filtered subset, so a chip's count always reflects
  // how many rows it would surface, independent of current filter).
  //
  // phantom_filed is a SUB-count within filed: a row that's filed AND
  // sendVerification === 'unverified' (no dmca_notice_targets and no
  // reply_token from an era that should have one) gets counted in
  // both 'filed' and 'phantom_filed', so the operator can see at a
  // glance how much of the Filed bucket is actually un-send-confirmed.
  const statusCounts = { all: leaks.length, unverified: 0, filed: 0, phantom_filed: 0, removed: 0, gone_independently: 0, acknowledged: 0, info_required: 0, contested: 0, bounce: 0, safe: 0 };
  for (const l of leaks) {
    statusCounts[leakStatus(l)] += 1;
    if (leakStatus(l) === 'filed' && sendVerification(l) === 'unverified') {
      statusCounts.phantom_filed += 1;
    }
  }

  // 2026-10-19: Route-class counts. is_safe=true rows are excluded
  // from the queue + counts (unless the operator flips includeSafe).
  // The phantom_filed count is a SUB-filter of the route colors —
  // a phantom row's primary color is still its route (green/amber/red),
  // but the phantom_filed chip surfaces just the dmca_filed-without-target
  // rows regardless of color.
  const routeQueueLeaks = includeSafe ? leaks : leaks.filter(l => l.is_safe !== true);
  const routeCounts = { all: routeQueueLeaks.length, green: 0, amber: 0, red: 0, pending: 0, grey: 0, phantom_filed: 0, legacy_unverified: 0, sent_no_response: 0 };
  for (const l of routeQueueLeaks) {
    const c = routeClass(l);
    if (c) routeCounts[c] += 1;
    if (isPhantomFiled(l)) routeCounts.phantom_filed += 1;
    if (isLegacyUnverified(l)) routeCounts.legacy_unverified += 1;
    if (isSentNoResponse(l)) routeCounts.sent_no_response += 1;
  }

  // Hosts present in THIS creator's leaks (the only hosts worth
  // offering — global host list would offer dead options).
  const hostsPresent = Array.from(new Set(leaks.map(l => host(l.leaked_url)))).sort();

  // Filter BEFORE sort so the sort order is over the filtered subset.
  // (Filter-then-sort vs sort-then-filter is the same for set membership
  // but cheaper, since sort works on a smaller array.)
  //
  // 'phantom_filed' chip is a sub-filter of 'filed': matches only filed
  // rows whose "unverified send" phantom-filed badge is visible (i.e.
  // sendVerification === 'unverified'). Matches the count semantics
  // above. The top-level 'unverified' chip flows through the else
  // branch and matches leakStatus() === 'unverified'.
  const filtered = leaks.filter(l => {
    // 2026-10-19: is_safe=true excluded from the colored queue by
    // default; includeSafe=true flips this. Runs FIRST because the
    // route-class filter below returns null for is_safe rows, which
    // would double-count the exclusion otherwise.
    if (!includeSafe && l.is_safe === true) return false;
    if (filterStatus === 'phantom_filed') {
      if (leakStatus(l) !== 'filed' || sendVerification(l) !== 'unverified') return false;
    } else if (filterStatus !== 'all' && leakStatus(l) !== filterStatus) {
      return false;
    }
    if (filterHost !== 'all' && host(l.leaked_url) !== filterHost) return false;
    // Route-class filter. 'phantom_filed' here is a sub-filter of the
    // route-color buckets (matches dmca_filed-without-target regardless
    // of color); the four color keys map 1:1 to routeClass() output.
    if (filterRoute !== 'all') {
      if (filterRoute === 'phantom_filed') {
        if (!isPhantomFiled(l)) return false;
      } else if (filterRoute === 'legacy_unverified') {
        if (!isLegacyUnverified(l)) return false;
      } else if (filterRoute === 'sent_no_response') {
        if (!isSentNoResponse(l)) return false;
      } else if (routeClass(l) !== filterRoute) {
        return false;
      }
    }
    return true;
  });
  const rows = sortLeaks(filtered, sortKey, sortDir);

  const filtersActive = filterStatus !== 'all' || filterHost !== 'all' || filterRoute !== 'all' || includeSafe;
  function clearFilters() {
    setFilterStatus('all');
    setFilterHost('all');
    setFilterRoute('all');
    setIncludeSafe(false);
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Leak Triage Console</h1>
      <p style={S.sub}>Per-creator leak feed. File DMCA · Mark Safe · Unmark.</p>

      <div style={S.panel}>
        <label style={S.label}>Creator</label>
        <select style={S.select} value={creatorId || ''} onChange={pickCreator}>
          <option value="">— select a creator —</option>
          {creators.map(c => (
            <option key={c.id} value={c.id}>{c.display}</option>
          ))}
        </select>

        {creator && (
          <div style={S.countsRow}>
            <div style={S.countItem}>
              <span style={{ ...S.countNum, color: '#fff' }}>{counts.total}</span>
              <span style={S.countLbl}>Total</span>
            </div>
            <div style={S.countItem}>
              <span style={{ ...S.countNum, color: '#34D399' }}>{counts.dmca_filed}</span>
              <span style={S.countLbl}>DMCA filed</span>
            </div>
            <div style={S.countItem}>
              <span style={{ ...S.countNum, color: '#FB923C' }}>{counts.unfiled}</span>
              <span style={S.countLbl}>Unfiled</span>
            </div>
            <div style={S.countItem}>
              <span style={{ ...S.countNum, color: '#888' }}>{counts.marked_safe}</span>
              <span style={S.countLbl}>Marked safe</span>
            </div>
          </div>
        )}
      </div>

      {creatorId && (
        <div style={S.panel}>
          <label style={S.label}>Paste leak URLs</label>
          <textarea
            style={S.textarea}
            placeholder={'https://example.com/leak1\nhttps://other.com/leak2\n…  (one per line, max 200)'}
            value={pasteUrls}
            onChange={e => setPasteUrls(e.target.value)}
            disabled={ingesting}
            spellCheck={false}
          />
          <input
            style={S.noteInput}
            placeholder='Source note (optional) — e.g. "tramp cluster - eporner family"'
            value={pasteNote}
            onChange={e => setPasteNote(e.target.value)}
            disabled={ingesting}
            maxLength={500}
          />
          <div>
            <button
              onClick={ingestUrls}
              disabled={ingesting || !pasteUrls.trim()}
              style={(ingesting || !pasteUrls.trim()) ? S.ingestBtnDis : S.ingestBtn}
            >
              {ingesting ? 'Ingesting…' : 'Ingest to dashboard'}
            </button>
          </div>
          <p style={S.hint}>
            Rows land as <code>status=detected</code>. Existing per-row File DMCA button fires them — nothing is auto-sent on ingest.
          </p>
        </div>
      )}

      {/* 2026-10-19: Route-color chip row in no-creator mode.
          Counts are the aggregate route_counts the /overview endpoint
          computes across ALL creators. Not interactive here (no leak
          table to filter in index mode) — purely a signal at the top
          of the admin page. The toggle below the chips flips the
          is_safe exclusion to match the per-creator chip behavior. */}
      {!creatorId && overviewRouteCounts && (
        <div style={S.filterPanel}>
          <p style={{ ...S.label, marginBottom: 10 }}>All creators · route-color aggregate</p>
          <div style={S.chipRow}>
            {ROUTE_CLASS_FILTERS.map(f => {
              const counts = includeSafe ? overviewRouteCountsIncludingSafe : overviewRouteCounts;
              const n = counts ? counts[f.key] : 0;
              return (
                <span
                  key={f.key}
                  style={S.chip}
                  title={`${f.label}: ${n} rows across all creators${includeSafe ? ' (including is_safe=true)' : ''}`}
                >
                  <span>{f.label}</span>
                  <span style={S.chipCount}>{n}</span>
                </span>
              );
            })}
            <button
              onClick={() => setIncludeSafe(v => !v)}
              style={{ ...S.includeSafeChip, ...(includeSafe ? S.includeSafeOn : null) }}
              title={includeSafe
                ? "Including is_safe=true rows in the aggregate counts."
                : "is_safe=true rows are excluded from the aggregate counts. Click to include."
              }
            >
              <span>{includeSafe ? '✓ Including safe' : 'Include safe'}</span>
              <span style={S.chipCount}>{overviewSafeCount}</span>
            </button>
          </div>
          <p style={{ ...S.hint, marginTop: 6 }}>
            Phantom-filed overlaps with the color buckets (dmca_filed=true, no dmca_notice_targets backing).
            Open a creator below to filter by color.
          </p>
        </div>
      )}

      {!creatorId && (
        <div style={S.panel}>
          <p style={{ ...S.label, marginBottom: 12 }}>All creators · sorted by unfiled count desc</p>
          {indexCounts === null ? (
            <p style={S.empty}>Loading creator counts…</p>
          ) : (
            (() => {
              const rows = creators
                .map(c => ({
                  creator: c,
                  ...(indexCounts[c.id] || { total: 0, unfiled: 0, marked_safe: 0 }),
                }))
                .sort((a, b) => b.unfiled - a.unfiled);
              if (rows.length === 0) return <p style={S.empty}>No creators.</p>;
              return (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Creator</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Unfiled</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Marked safe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr
                        key={r.creator.id}
                        style={{
                          background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/admin/leaks/${r.creator.id}`)}
                      >
                        <td style={{ ...S.td, color: '#A78BFA' }}>{r.creator.display}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: r.unfiled > 0 ? '#FB923C' : '#555', fontWeight: r.unfiled > 0 ? 600 : 400 }}>{r.unfiled}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{r.total}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: '#888' }}>{r.marked_safe}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()
          )}
        </div>
      )}

      {creatorId && loading && <p style={S.empty}>Loading…</p>}

      {creatorId && !loading && leaks.length > 0 && (
        <div style={S.filterPanel}>
          <div style={S.chipRow}>
            {STATUS_FILTERS.map(f => {
              const active = filterStatus === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilterStatus(f.key)}
                  style={{ ...S.chip, ...(active ? S.chipActive : null) }}
                  title={`Filter by ${f.label.toLowerCase()}`}
                >
                  <span>{f.label}</span>
                  <span style={{ ...S.chipCount, ...(active ? S.chipCountActive : null) }}>
                    {statusCounts[f.key]}
                  </span>
                </button>
              );
            })}
            <span style={S.filterCount}>
              {filtersActive
                ? `Showing ${rows.length} of ${leaks.length}`
                : `${leaks.length} total`}
            </span>
          </div>
          {/* 2026-10-19: Route-class chip row. Second row of chips, same
              visual style as the status chips, with counts drawn from the
              routeCounts object (excludes is_safe by default). The
              include-safe toggle sits at the end — flipping it folds
              is_safe=true rows back into both the counts and the queue. */}
          <div style={{ ...S.chipRow, marginTop: 10 }}>
            {ROUTE_CLASS_FILTERS.map(f => {
              const active = filterRoute === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilterRoute(f.key)}
                  style={{ ...S.chip, ...(active ? S.chipActive : null) }}
                  title={`Filter by ${f.label.toLowerCase()}`}
                >
                  <span>{f.label}</span>
                  <span style={{ ...S.chipCount, ...(active ? S.chipCountActive : null) }}>
                    {routeCounts[f.key]}
                  </span>
                </button>
              );
            })}
            <button
              onClick={() => setIncludeSafe(v => !v)}
              style={{ ...S.includeSafeChip, ...(includeSafe ? S.includeSafeOn : null) }}
              title={includeSafe
                ? "Including is_safe=true rows in the colored queue + counts."
                : "is_safe=true rows are excluded from the colored queue + counts. Click to include."
              }
            >
              <span>{includeSafe ? '✓ Including safe' : 'Include safe'}</span>
              <span style={{ ...S.chipCount, ...(includeSafe ? S.chipCountActive : null) }}>
                {statusCounts.safe}
              </span>
            </button>
          </div>
          <div style={S.hostFilterRow}>
            <label style={S.label}>Host</label>
            <select
              style={S.hostSelect}
              value={filterHost}
              onChange={e => setFilterHost(e.target.value)}
            >
              <option value="all">All hosts ({hostsPresent.length})</option>
              {hostsPresent.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            {filtersActive && (
              <button onClick={clearFilters} style={S.clearBtn}>Clear filters</button>
            )}
          </div>
        </div>
      )}

      {creatorId && !loading && leaks.length === 0 && (
        <p style={S.empty}>No leaks for this creator.</p>
      )}

      {creatorId && !loading && leaks.length > 0 && rows.length === 0 && (
        <p style={S.empty}>0 of {leaks.length} leaks match the current filter.</p>
      )}

      {creatorId && !loading && rows.length > 0 && (
        <div style={S.panel}>
          <table style={S.table}>
            <thead>
              <tr>
                <SortableTh label="URL"      sortKey="leaked_url"  currentKey={sortKey} currentDir={sortDir} onSort={handleSort} style={{ width: '40%' }} />
                <SortableTh label="Host"     sortKey="host"        currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableTh label="Route"    sortKey="routeClass"  currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableTh label="Detected" sortKey="detected_at" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableTh label="Status"   sortKey="status"      currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l, i) => {
                const filed    = !!l.dmca_filed_at;
                const safe     = l.is_safe === true;
                const reply    = l.reply_status || null;
                // 2026-10-05: split "gone independently" out of "removed".
                // A row with removed_at set AND removal_source='found_gone_on_check'
                // is gone, but it's not a removal win — label distinctly.
                // Pre-migration (column missing): removal_source===undefined,
                // so goneIndependently stays false and the row renders as
                // 'removed' per legacy behavior.
                const goneIndependently = !!l.removed_at && l.removal_source === 'found_gone_on_check';
                const removed  = (!!l.removed_at && !goneIndependently) || reply === 'removed';
                const terminal = safe || removed || goneIndependently;     // File DMCA / Mark Safe hidden on terminal rows. Order matches leakStatus() precedence: is_safe wins over removed_at, so a row that's both shows as Safe.
                const pending  = pendingFire[l.id];
                const remain   = pending ? Math.max(0, Math.ceil((pending.confirmAt - Date.now()) / 1000)) : 0;
                const dim      = safe ? { opacity: 0.5 } : {};
                // Send-verification sub-state — only meaningful when
                // the row displays the filed-without-reply badge, i.e.
                // !safe && !reply && filed. Splits the legacy single
                // "✓ Filed" badge into confirmed_sent / unverified /
                // unknowable so a phantom-filed can't pose as a real
                // send. Escalation-age (filedDays) shown ONLY for
                // confirmed_sent — never on unverified or unknowable,
                // since neither has an actual send to time.
                const verify    = (!safe && !reply && filed) ? sendVerification(l) : null;
                const filedDays = verify === 'confirmed_sent' ? ageDays(l.dmca_filed_at) : null;
                // Status precedence (updated 2026-09-29):
                //   1. is_safe — operator dismissal wins over everything,
                //      including observed removal. If a row was marked safe
                //      earlier, that judgment is authoritative; removed
                //      info surfaces via tooltip.
                //   2. removed_at — observed removal (host reply OR liveness
                //      probe OR inherited from sibling). Wins over
                //      reply_status because removed_at is the authoritative
                //      "gone" column (Task 4 constraint 2026-09-29).
                //   3. reply_status from the inbound DMCA parser
                //      (removed > acknowledged > contested > bounce).
                //   4. dmca_filed_at — sent, no reply yet.
                //   5. Unverified — nothing tried yet (formerly "Pending").
                // 2026-10-19: Route-class color coding. Left-border tint
                // on the row (non-destructive — status badges keep their
                // own column) + a text label in the Route cell. Phantom-
                // filed rows (dmca_filed=true, no notice_target backing)
                // keep their route color and get a small "filed?" tag.
                const rc = routeClass(l);
                const phantom = isPhantomFiled(l);
                const legacyUnverified = isLegacyUnverified(l);
                const sentNoResponse = isSentNoResponse(l);
                // Days-since-latest-sent for the "pending Nd" label.
                // Only computed when rc === 'pending'; falls back to null
                // otherwise so the ternary below renders cleanly.
                const pendingDays = rc === 'pending' && l.latest_sent_at
                  ? Math.floor((Date.now() - Date.parse(l.latest_sent_at)) / 86400000)
                  : null;
                const borderStyle =
                  rc === 'green'   ? S.routeBorderGreen :
                  rc === 'amber'   ? S.routeBorderAmber :
                  rc === 'red'     ? S.routeBorderRed   :
                  rc === 'pending' ? S.routeBorderPending :
                  rc === 'grey'    ? S.routeBorderGrey  : null;
                const routeLabelStyle =
                  rc === 'green'   ? S.routeLabelGreen :
                  rc === 'amber'   ? S.routeLabelAmber :
                  rc === 'red'     ? S.routeLabelRed   :
                  rc === 'pending' ? S.routeLabelPending :
                  rc === 'grey'    ? S.routeLabelGrey  : null;
                const routeLabelText =
                  rc === 'green'   ? 'Email'  :
                  rc === 'amber'   ? 'Form'   :
                  rc === 'red'     ? 'None'   :
                  rc === 'pending' ? (pendingDays != null ? `pending ${pendingDays}d` : 'pending') :
                  rc === 'grey'    ? 'Removed' : '—';
                return (
                  <tr key={l.id} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)', ...borderStyle, ...dim }}>
                    <td style={{ ...S.td, maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a href={l.leaked_url} target="_blank" rel="noreferrer" style={S.link}>{l.leaked_url}</a>
                    </td>
                    <td style={S.td}>{host(l.leaked_url)}</td>
                    <td style={S.td}>
                      <span style={routeLabelStyle}>{routeLabelText}</span>
                      {phantom && (
                        <span
                          style={S.badgePhantomTag}
                          title="dmca_filed=true but no dmca_notice_targets row backs it. Looks filed, no send evidence. See project_phantom_filed_213_rows."
                        >filed?</span>
                      )}
                      {legacyUnverified && (
                        <span
                          style={S.badgeLegacyUnverifiedTag}
                          title="dmca_filed=true in a pre-tracking era (before 2026-05-15 OR SendTracking era without reply_token) AND no reconstructible send evidence (no reply_token, filing_method, filed_by, or 'DMCA filed to…' in notes). Not phantom-filed — the schema wasn't tracking yet — but also no audit trail. Doesn't grey out; surfaces here so the operator can spot it."
                        >legacy, unverified</span>
                      )}
                      {sentNoResponse && (
                        <span
                          style={S.badgeSentNoResponseTag}
                          title={`Latest sent notice is >30 days old (${l.latest_sent_at ? l.latest_sent_at.slice(0,10) : '(unknown)'}) and the host hasn't responded or removed. Row keeps its route color; this flag highlights it for re-notice / escalation.`}
                        >sent, no response</span>
                      )}
                    </td>
                    <td style={S.td}>{fmt(l.detected_at)}</td>
                    <td style={S.td}>
                      {l.duplicate_of && (
                        <span style={S.badgeDuplicate} title={`Scanner-duplicate of ${String(l.duplicate_of).slice(0,8)} — hidden from creator view.`}>⊟ duplicate</span>
                      )}
                      {' '}
                      {safe && (
                        <span
                          style={S.badgeSafe}
                          title={
                            reply === 'removed' || l.removed_at
                              ? `Operator marked safe (wins over removed).${l.removed_at ? ` Observed removed at ${fmt(l.removed_at)}.` : ''}${reply ? ` Host reply: ${reply}.` : ''}`
                              : reply
                                ? `Operator marked safe. Host also replied: ${reply}.`
                                : 'Operator marked safe.'
                          }
                        >↩ Safe</span>
                      )}
                      {!safe && removed && (
                        <span
                          style={S.badgeRemoved}
                          title={
                            reply === 'removed'
                              ? `Host confirmed removal via DMCA reply${l.removed_at ? ` (removed_at ${fmt(l.removed_at)})` : ''}.`
                              : l.removed_at
                                ? `Observed removed at ${fmt(l.removed_at)} (liveness probe, inherited from sibling, or manual stamp — no host reply required).`
                                : '✓ Removed'
                          }
                        >✓ Removed</span>
                      )}
                      {!safe && !removed && goneIndependently && (
                        <span
                          style={S.badgeGoneIndependent}
                          title={`URL went gone independently of our action${l.removed_at ? ` (observed at ${fmt(l.removed_at)})` : ''}. Not credited as a removal win in weekly summaries.`}
                        >◌ No longer online</span>
                      )}
                      {!safe && !removed && !goneIndependently && reply === 'acknowledged'  && <span style={S.badgeAcked} title="Host received the notice; awaiting takedown.">◷ Acknowledged</span>}
                      {!safe && !removed && reply === 'info_required' && <span style={S.badgeInfoReq} title="Host asked for a missing § 512(c)(3) element or a direct video URL — re-file with the missing field.">? Info requested</span>}
                      {!safe && !removed && reply === 'contested'     && <span style={S.badgeContested} title="Host pushed back on the notice — operator review needed.">⚠ Contested</span>}
                      {!safe && !removed && reply === 'bounce'        && <span style={S.badgeBounce} title="Delivery failed — DMCA never reached the host.">✗ Delivery failed</span>}
                      {!safe && !removed && verify === 'confirmed_sent' && (
                        <span
                          style={S.badgeSent}
                          title={`Send confirmed via ${
                            (Array.isArray(l.sent_routes) && l.sent_routes.length)
                              ? l.sent_routes.join(' + ')
                              : (l.has_notice_target ? 'dmca_notice_targets row exists' : 'reply_token stamped')
                          }. Filed ${fmt(l.dmca_filed_at)}${filedDays != null ? ' — ' + filedDays + ' days ago' : ''}. No host reply parsed yet.`}
                        >
                          ✓ Sent{
                            Array.isArray(l.sent_routes) && l.sent_routes.length > 1
                              ? ` · ${l.sent_routes.length} channels`
                              : (Array.isArray(l.sent_routes) && l.sent_routes.length === 1 ? ` · ${l.sent_routes[0]}` : '')
                          }{filedDays != null ? ` · ${filedDays}d` : ''}
                        </span>
                      )}
                      {!safe && !removed && verify === 'unverified' && (
                        <span style={S.badgeUnverified} title={`Filed ${fmt(l.dmca_filed_at)} but NO send-confirmation record (no dmca_notice_targets, no reply_token) and the row's era SHOULD have one. Likely phantom-filed — see project_phantom_filed_213_rows. Treat as needs-re-file consideration, NOT escalation.`}>
                          ⚠ Unverified send
                        </span>
                      )}
                      {!safe && !removed && verify === 'unknowable' && (
                        <span style={S.badgeUnknowable} title={`Filed ${fmt(l.dmca_filed_at)} — before send-tracking was instrumented (reply_token added 2026-05-15). Cannot determine real-send vs phantom from data alone. Distinct from ⚠ Unverified send: this is "we don't know," not "likely phantom."`}>
                          Filed · pre-tracking
                        </span>
                      )}
                      {!safe && !removed && !reply && !filed && <span style={S.badgePending} title="No notice sent, no operator dismissal, no observed removal.">· Unverified</span>}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      {/* Terminal states (Removed / Safe): no re-fire, no mark-safe.
                          Safe rows offer Unmark. Removed rows just show Look up.
                          If a row is both removed AND safe (rare), Unmark still
                          appears since safe is the operator's own dismissal.

                          Non-terminal states (Unverified / Filed / Acknowledged /
                          Contested / Bounce / Phantom-Filed): File DMCA + Mark Safe
                          both offered. Cooldown guard in fireDmcaForLeak blocks a
                          rapid re-fire on rows that already have a recent successful
                          send (returns 429 + Retry-After); operator can File DMCA
                          again after cooldown expires. Contested/Bounce re-fires
                          are the primary intended use — new evidence or fresh route.
                      */}
                      {!terminal && (
                        <>
                          <button
                            onClick={() => onFireClick(l)}
                            style={pending ? S.btnConfirm : S.btn}
                            title="3-second confirm-click pattern"
                          >
                            {pending ? `Click again (${remain}s)` : 'File DMCA'}
                          </button>
                          {' '}
                          <button onClick={() => openDomainLookup(l)} style={S.btnLookup}>Look up</button>
                          {' '}
                          <button onClick={() => markSafe(l)} style={S.btnSafe}>Mark Safe</button>
                        </>
                      )}
                      {terminal && (
                        <>
                          <button onClick={() => openDomainLookup(l)} style={S.btnLookup}>Look up</button>
                          {safe && (
                            <>
                              {' '}
                              <button onClick={() => unmarkSafe(l)} style={S.btnSafe}>Unmark</button>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lookup && (
        <DomainLookupModal
          lookup={lookup}
          onClose={() => setLookup(null)}
          onRefetch={() => lookup._leak && openDomainLookup(lookup._leak, true)}
          onRetry={() => lookup._leak && openDomainLookup(lookup._leak, false)}
          onCopy={copyAuthoritative}
        />
      )}

      {toast && (
        <div style={{ ...S.toast, background: toast.kind === 'ok' ? '#16a34a' : '#dc2626' }}>
          {toast.text}
        </div>
      )}
    </div>
  );
}
